// ============================================
// AVISOMARGENLISTA.JS — ALERTA-BAJO-COSTO: el margen REAL de una lista con precio congelado
// ============================================
// POR QUE (Ricardo, 2026-10-13): en la lista de Alianza habia dos productos que se vendian bajo el
// costo de HOY y seis con menos del 10%, y nada lo hacia visible. Aqui viven las tres piezas que lo
// enseñan: el CHIP de cada linea, el AVISO de arriba de la lista y la VENTANA de la perdida.
//
// Dos casos que NO se pueden mezclar, porque es lo que decide si el aviso sirve o se vuelve ruido:
//   - bajo costo SIN marcar  -> rojo, alarmante. "Esto se nos colo."
//   - a perdida, ACEPTADA    -> sello discreto. "Esto lo decidi yo." Se ve, pero no grita.
// Lo que necesita accion va ARRIBA y separado de lo ya decidido.
//
// El estado lo calcula el SERVIDOR (services/margenListas.js) contra el costo de hoy. Aqui no se
// decide nada: si cada pantalla calculara su "bajo costo", contarian cosas distintas. Quien no
// puede ver costos recibe todo esto en null (mascara del servidor) y no se pinta nada.

import React, { useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { fmtDinero } from '../utils/dinero';

const PP = "'Poppins', sans-serif";

export const TONO_MARGEN = {
  bajo_costo:       { fondo: '#c0392b', texto: '#ffffff', borde: '#c0392b' },
  margen_bajo:      { fondo: '#fff4dc', texto: '#8a5a1b', borde: '#f0d59a' },
  perdida_aceptada: { fondo: '#f4f5f6', texto: '#555d65', borde: '#d5d9dd' },
  sin_costo:        { fondo: '#f4f5f6', texto: '#6b7379', borde: '#e0e3e6' },
};

const pct = (n) => `${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

function fmtFechaHora(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
}

// Texto corto del chip. null = la linea no lleva chip (margen sano o sin precio pactado vigente).
export function textoChipMargen(e) {
  if (!e) return null;
  if (e.estado === 'bajo_costo') return `Bajo costo · pierdes ${fmtDinero(e.costo_perdida_unidad_mxn)}/pza`;
  if (e.estado === 'perdida_aceptada') return 'A pérdida, aceptado';
  if (e.estado === 'margen_bajo') return `Margen ${pct(e.margen_real_pct)}`;
  if (e.estado === 'sin_costo') return 'Sin costo';
  return null;
}

// CHIP de una linea. Rojo y perdida aceptada abren la ventana (para marcar o para leer el motivo).
export function ChipMargen({ estado, onClick, compacto = false }) {
  const texto = textoChipMargen(estado);
  if (!texto) return null;
  const t = TONO_MARGEN[estado.estado];
  const clicable = !!onClick && (estado.estado === 'bajo_costo' || estado.estado === 'perdida_aceptada');
  const titulo = estado.estado === 'bajo_costo'
    ? `Se vende por debajo del costo de hoy (${fmtDinero(estado.costo_hoy_mxn)}). Toca para ver o marcar como pérdida aceptada.`
    : estado.estado === 'perdida_aceptada' ? 'Pérdida aceptada por dirección. Toca para ver el motivo.'
    : estado.estado === 'margen_bajo' ? `Margen real contra el costo de hoy (${fmtDinero(estado.costo_hoy_mxn)}).`
    : 'Este producto no tiene costo registrado: no se puede calcular su margen.';
  const Icono = estado.estado === 'bajo_costo' ? LucideIcons.AlertOctagon
    : estado.estado === 'perdida_aceptada' ? LucideIcons.Stamp
    : estado.estado === 'margen_bajo' ? LucideIcons.TrendingDown
    : LucideIcons.HelpCircle;
  const Tag = clicable ? 'button' : 'span';
  return (
    <Tag
      type={clicable ? 'button' : undefined}
      onClick={clicable ? onClick : undefined}
      title={titulo}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: '100%',
        padding: compacto ? '2px 8px' : '4px 10px', borderRadius: 6,
        background: t.fondo, color: t.texto, border: `1px solid ${t.borde}`,
        fontSize: compacto ? 10.5 : 11, fontWeight: estado.estado === 'bajo_costo' ? 800 : 700,
        fontFamily: PP, letterSpacing: 0.2, lineHeight: 1.3,
        cursor: clicable ? 'pointer' : 'default', textAlign: 'left',
        ...(clicable ? { minHeight: compacto ? 26 : 30 } : {}),
      }}
    >
      <Icono size={compacto ? 12 : 13} strokeWidth={2.4} style={{ flexShrink: 0 }} />
      <span style={{ overflowWrap: 'anywhere' }}>{texto}</span>
    </Tag>
  );
}

// AVISO de arriba de la lista. Cada trozo filtra la lista a esas lineas.
//   "1 bajo costo sin revisar · 6 con margen menor al 10%"   (lo que necesita accion)
//   "Ya decidido: 1 a pérdida aceptada (pierdes $1,659.90 por pieza)"
export function AvisoMargenLista({ resumen, filtro, onFiltro }) {
  if (!resumen) return null;
  const { bajo_costo = 0, margen_bajo = 0, perdida_aceptada = 0, sin_costo = 0, umbral = 10 } = resumen;
  if (!bajo_costo && !margen_bajo && !perdida_aceptada && !sin_costo) return null;
  const accion = bajo_costo > 0 || margen_bajo > 0;

  const Trozo = ({ clave, children, color }) => (
    <button
      type="button"
      onClick={() => onFiltro(filtro === clave ? null : clave)}
      style={{
        background: filtro === clave ? '#ffffff' : 'transparent', border: filtro === clave ? '1px solid currentColor' : '1px solid transparent',
        borderRadius: 6, padding: '4px 6px', margin: '-4px 0', minHeight: 32,
        color, fontFamily: PP, fontSize: 13, cursor: 'pointer', textAlign: 'left',
        textDecoration: 'underline', textUnderlineOffset: 3,
      }}
    >
      {children}
    </button>
  );

  return (
    <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {accion && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px 16px', flexWrap: 'wrap',
          padding: '10px 14px', borderRadius: 10,
          background: bajo_costo > 0 ? '#fdecec' : '#fff7e8',
          border: `1px solid ${bajo_costo > 0 ? '#f3c9c9' : '#f0d59a'}`,
          fontFamily: PP, fontSize: 13, color: '#4e5357',
        }}>
          <LucideIcons.AlertOctagon size={18} color={bajo_costo > 0 ? '#c0392b' : '#c47d12'} style={{ flexShrink: 0 }} />
          {bajo_costo > 0 && (
            <Trozo clave="bajo_costo" color="#a32d22">
              <strong>{bajo_costo}</strong> {bajo_costo === 1 ? 'línea se vende' : 'líneas se venden'} por debajo del costo <strong>sin revisar</strong>
              {resumen.costo_perdida_sin_revisar_unidad_mxn > 0 && ` (pierdes ${fmtDinero(resumen.costo_perdida_sin_revisar_unidad_mxn)} por pieza)`}
            </Trozo>
          )}
          {margen_bajo > 0 && (
            <Trozo clave="margen_bajo" color="#8a5a1b">
              <strong>{margen_bajo}</strong> con margen menor al {umbral}%
            </Trozo>
          )}
        </div>
      )}
      {(perdida_aceptada > 0 || sin_costo > 0) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px 16px', flexWrap: 'wrap',
          padding: '8px 14px', borderRadius: 10, background: '#f7f8f9', border: '1px solid #e6e8ea',
          fontFamily: PP, fontSize: 12.5, color: '#5e666e',
        }}>
          {perdida_aceptada > 0 && (
            <>
              <span style={{ fontWeight: 700 }}>Ya decidido:</span>
              <Trozo clave="perdida_aceptada" color="#555d65">
                {perdida_aceptada} a pérdida aceptada
                {resumen.costo_perdida_aceptada_unidad_mxn > 0 && ` (pierdes ${fmtDinero(resumen.costo_perdida_aceptada_unidad_mxn)} por pieza)`}
              </Trozo>
            </>
          )}
          {sin_costo > 0 && (
            <>
            <span style={{ fontWeight: 700 }}>Falta el dato:</span>
            <Trozo clave="sin_costo" color="#555d65">
              {sin_costo} sin costo registrado (sin margen hasta tenerlo)
            </Trozo>
            </>
          )}
        </div>
      )}
      {filtro && (
        <div style={{ fontFamily: PP, fontSize: 12, color: '#5e666e' }}>
          Mostrando solo esas líneas ·{' '}
          <button type="button" onClick={() => onFiltro(null)} style={{
            background: 'none', border: 'none', padding: '6px 2px', color: '#2f5f9e', fontFamily: PP,
            fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline',
          }}>Ver todas</button>
        </div>
      )}
    </div>
  );
}

// VENTANA de la perdida: para MARCAR (linea en rojo) o para LEER el motivo y la firma (sello).
// `puedeMarcar` lo decide quien la abre (direccion); el candado de verdad esta en el servidor.
export function ModalPerdida({ item, puedeMarcar, guardando, error, onCerrar, onGuardar, onQuitar }) {
  const [motivo, setMotivo] = useState('');
  const e = item && item.margen_estado;
  if (!e) return null;
  const aceptada = e.estado === 'perdida_aceptada';
  const precio = Number(item.precio_congelado_mxn) || 0;
  const fila = (etiqueta, valor, fuerte = false) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid #eef0f2', fontSize: 13 }}>
      <span style={{ color: '#5e666e' }}>{etiqueta}</span>
      <span style={{ fontWeight: fuerte ? 800 : 600, color: fuerte ? '#a32d22' : '#2e3338', textAlign: 'right' }}>{valor}</span>
    </div>
  );
  const boton = {
    minHeight: 40, padding: '9px 14px', borderRadius: 8, fontFamily: PP, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  };

  return (
    <>
      <div onClick={onCerrar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400 }} />
      <div role="dialog" aria-modal="true" style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 401,
        width: 'min(94vw, 480px)', maxHeight: 'calc(var(--beep-vh) * 90)',
        display: 'flex', flexDirection: 'column', background: '#ffffff', borderRadius: 12,
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)', fontFamily: PP, color: '#4e5357',
      }}>
        <div style={{ padding: '18px 20px 8px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          {aceptada
            ? <LucideIcons.Stamp size={22} color="#555d65" style={{ flexShrink: 0 }} />
            : <LucideIcons.AlertOctagon size={22} color="#c0392b" style={{ flexShrink: 0 }} />}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#2e3338' }}>
              {aceptada ? 'Pérdida aceptada' : 'Se vende por debajo del costo'}
            </div>
            <div style={{ fontSize: 12.5, color: '#5e666e', overflowWrap: 'anywhere' }}>
              {item.modelo ? `${item.modelo} · ` : ''}{item.nombre}
            </div>
          </div>
          <button type="button" onClick={onCerrar} aria-label="Cerrar" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, color: '#5e666e' }}>
            <LucideIcons.X size={18} />
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '4px 20px 8px' }}>
          {fila('Precio pactado', fmtDinero(precio))}
          {fila('Costo de hoy', fmtDinero(e.costo_hoy_mxn))}
          {fila('Pierdes por pieza hoy', fmtDinero(e.costo_perdida_unidad_mxn), true)}
          {fila('Margen real', pct(e.margen_real_pct))}
          {aceptada && e.costo_perdida_al_aceptar_mxn != null && fila('Pérdida por pieza cuando se aceptó', fmtDinero(e.costo_perdida_al_aceptar_mxn))}

          {aceptada ? (
            <div style={{ marginTop: 12, background: '#f7f8f9', border: '1px solid #e6e8ea', borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 12, color: '#5e666e', marginBottom: 4 }}>Motivo</div>
              <div style={{ fontSize: 14, color: '#2e3338', fontWeight: 600, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {item.perdida_aceptada_motivo}
              </div>
              <div style={{ fontSize: 12, color: '#5e666e', marginTop: 8 }}>
                Lo aceptó {item.perdida_aceptada_por_nombre || 'alguien de dirección'} el {fmtFechaHora(item.perdida_aceptada_at)}.
              </div>
              <div style={{ fontSize: 11.5, color: '#9aa3ab', marginTop: 6 }}>
                Vale para este precio. Si se cambia el precio, la línea vuelve a avisar en rojo.
              </div>
            </div>
          ) : puedeMarcar ? (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12.5, color: '#5e666e', marginBottom: 6 }}>
                Si esta pérdida es a propósito, márcala. Deja de avisar en rojo y el vigilante no te la vuelve a contar.
                Queda firmada contigo y con la fecha, y el motivo se ve al tocar el sello.
              </div>
              <textarea
                value={motivo}
                onChange={(ev) => setMotivo(ev.target.value)}
                rows={3}
                placeholder="Por qué se acepta (p. ej.: acuerdo comercial con Alianza)"
                style={{ width: '100%', boxSizing: 'border-box', fontFamily: PP, fontSize: 14, padding: 10, borderRadius: 8, border: '1px solid #d5d9dd', resize: 'vertical' }}
              />
            </div>
          ) : (
            <div style={{ marginTop: 12, fontSize: 12.5, color: '#5e666e' }}>
              Solo dirección puede marcarla como pérdida aceptada.
            </div>
          )}
          {error && <div style={{ color: '#c0392b', fontSize: 12.5, marginTop: 8 }}>{error}</div>}
        </div>

        <div style={{ padding: '10px 20px 16px', display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end', borderTop: '1px solid #eef0f2' }}>
          <button type="button" onClick={onCerrar} style={{ ...boton, background: '#ffffff', color: '#4e5357', border: '1px solid #d5d9dd' }}>
            Cerrar
          </button>
          {aceptada && puedeMarcar && (
            <button type="button" disabled={guardando} onClick={onQuitar} style={{ ...boton, background: '#ffffff', color: '#a32d22', border: '1px solid #f3c9c9', opacity: guardando ? 0.6 : 1 }}>
              Quitar la marca
            </button>
          )}
          {!aceptada && puedeMarcar && (
            <button type="button" disabled={guardando || motivo.trim().length < 5} onClick={() => onGuardar(motivo.trim())}
              style={{ ...boton, background: '#555d65', color: '#ffffff', border: 'none', opacity: (guardando || motivo.trim().length < 5) ? 0.5 : 1 }}>
              Marcar como pérdida aceptada
            </button>
          )}
        </div>
      </div>
    </>
  );
}
